import { useEffect, useState } from 'react';
import { Collapse, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { LeaveTable } from './leave-table';
import { useTable } from '@app/components/table';
import { AddLeave } from './add-leave-form';
import { useDialog, useRequestState, useSearch } from '@app/hooks';
import { selectLeaveState } from '../selectors';
import { getLeaves } from '../slice';
import { useAuthRole, useAuthRolePermissions } from '@app/utils';
import { getDashboardData, selectDashboardState } from '@app/modules/dashboard';
import { EmployeeLeavesStatus } from '@app/modules/employee-dashboard';
import { AddExistingLeave } from './add-existing-leave';

const AddLeaveEmployee = () => {
  const dispatch = useDispatch();
  const tableProps = useTable();
  const leaveDialog = useDialog();
  const addExistingLeaveDialog = useDialog();
  const showChart = useDialog();
  const [chartVisible, setChartVisible] = useState(false);
  const { isSuperAdmin, isManager } = useAuthRole();
  const [itSelf, setItSelf] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('leaveViewType');
    if (isSuperAdmin) {
      setItSelf(false);
    } else if (stored === 'team') {
      setItSelf(false);
    } else {
      setItSelf(true);
    }
  }, []);

  useEffect(() => {
    dispatch(getDashboardData());
    dispatch(getLeaves(isSuperAdmin ? false : itSelf));
  }, [dispatch, isSuperAdmin, itSelf]);

  const { data, loading } = useRequestState({
    stateSelector: selectLeaveState,
    errorShown: true,
  });

  const { data: leavesData } = useRequestState({
    stateSelector: selectDashboardState,
    errorShown: true,
  });

  const modifiedData = data?.map((data) => ({
    ...data,
    employee: `${data.user.firstName} ${data.user.lastName}`,
  }));

  const searchProps = useSearch({
    data: modifiedData,
    fields: ['employee', 'leaveType', 'id'],
  });

  const handleDropDownOption = (option: any) => {
    const self = option === 'Self Leaves';
    setItSelf(self);
    localStorage.setItem('leaveViewType', self ? 'self' : 'team');
    setChartVisible(false);
  };

  const toggleDetailsVisibility = () => {
    setChartVisible(!chartVisible);
  };

  const { hasPermission } = useAuthRolePermissions();
  const canAddLeaves = hasPermission('Leaves', 'w') && !isSuperAdmin;

  return (
    <>
      <Header
        title={Strings.sidebar.leaves}
        helperText=""
        actionButtonTitle={canAddLeaves ? Strings.button.addLeave : 'Add Existing Leave'}
        onClickActionButton={canAddLeaves ? leaveDialog.show : addExistingLeaveDialog.show}
        additionalButtonTitle={
          !isSuperAdmin && ((isManager && itSelf) || !isManager)
            ? chartVisible
              ? 'Hide Chart'
              : 'Show Chart'
            : undefined
        }
        dropDownSelect={isManager ? (itSelf ? 'Self Leaves' : "Team Member's Leaves") : undefined}
        dropDownOptions={isManager ? ['Self Leaves', "Team Member's Leaves"] : []}
        onClickAdditionalButton={() => {
          toggleDetailsVisibility();
          showChart.show();
        }}
        onClickDropDownOption={handleDropDownOption}
      />
      <Stack>
        <Collapse in={chartVisible}>
          <EmployeeLeavesStatus data={leavesData} />
        </Collapse>
      </Stack>
      <Stack>
        <Header
          searchBarProps={{
            placeholder: Strings.common.search,
            onChange: searchProps.handleSearchChange,
          }}
        />
        <LeaveTable
          tableProps={tableProps}
          rows={searchProps.filteredRows}
          loading={loading}
          itSelf={itSelf}
        />
      </Stack>
      {addExistingLeaveDialog.visible && (
        <AddExistingLeave open onClose={addExistingLeaveDialog.hide} />
      )}
      {leaveDialog.visible && <AddLeave open onClose={leaveDialog.hide} />}
    </>
  );
};

export { AddLeaveEmployee };
